import fs from 'fs/promises';
import os from 'os';
import path from 'path';

import type { Logger } from 'pino';

export interface RootCandidate {
  path: string;
  source: 'cli-arg' | 'env-coderule-root' | 'env-workspace' | 'process-cwd';
  baseConfidence: number;
  modifiers: Array<{ reason: string; delta: number }>;
  finalConfidence: number;
  exists: boolean;
  isDirectory: boolean;
}

export interface RootResolverOptions {
  cliRoot?: string;
  logger?: Logger;
}

interface CandidateSource {
  value: string | undefined;
  source: RootCandidate['source'];
  baseConfidence: number;
}

function collectCandidateSources(
  options?: RootResolverOptions,
): CandidateSource[] {
  const sources: CandidateSource[] = [];

  // Priority 1: CLI argument
  if (options?.cliRoot) {
    sources.push({
      value: options.cliRoot,
      source: 'cli-arg',
      baseConfidence: 1.0,
    });
  }

  // Priority 2: CODERULE_ROOT env
  if (process.env.CODERULE_ROOT) {
    sources.push({
      value: process.env.CODERULE_ROOT,
      source: 'env-coderule-root',
      baseConfidence: 0.9,
    });
  }

  // Priority 3: WORKSPACE_FOLDER_PATHS env (treated as single path)
  if (process.env.WORKSPACE_FOLDER_PATHS) {
    sources.push({
      value: process.env.WORKSPACE_FOLDER_PATHS,
      source: 'env-workspace',
      baseConfidence: 0.8,
    });
  }

  // Priority 4: Current working directory
  sources.push({
    value: process.cwd(),
    source: 'process-cwd',
    baseConfidence: 0.6,
  });

  return sources;
}

async function pathExists(targetPath: string): Promise<boolean> {
  try {
    await fs.access(targetPath);
    return true;
  } catch {
    return false;
  }
}

async function isDirectory(targetPath: string): Promise<boolean> {
  try {
    const stat = await fs.stat(targetPath);
    return stat.isDirectory();
  } catch {
    return false;
  }
}

async function hasGitDirectory(rootPath: string): Promise<boolean> {
  const gitPath = path.join(rootPath, '.git');
  return pathExists(gitPath);
}

function isShallowPath(absolutePath: string): boolean {
  // Count separators in the absolute path
  // Windows: C:\ has 1 separator (after C:)
  // Unix: / has 0 separators after the root
  const normalized = absolutePath.replace(/\\/g, '/');
  const separatorCount = (normalized.match(/\//g) || []).length;

  // If path has only 1 or 0 separator, it's likely a root directory
  return separatorCount <= 1;
}

function isHomeDirectory(candidatePath: string): boolean {
  const home = os.homedir();
  const normalizedCandidate = path.normalize(candidatePath);
  const normalizedHome = path.normalize(home);
  return normalizedCandidate === normalizedHome;
}

async function applyModifiers(
  candidate: RootCandidate,
): Promise<RootCandidate> {
  const modifiers: Array<{ reason: string; delta: number }> = [];

  // Modifier A: .git directory exists → +0.1
  if (await hasGitDirectory(candidate.path)) {
    modifiers.push({ reason: 'has .git directory', delta: 0.1 });
  }

  // Modifier B: Shallow path (likely system root) → -0.2
  if (isShallowPath(candidate.path)) {
    modifiers.push({ reason: 'shallow path (likely root)', delta: -0.2 });
  }

  // Modifier C: Home directory → -0.3
  if (isHomeDirectory(candidate.path)) {
    modifiers.push({ reason: 'is home directory', delta: -0.3 });
  }

  const totalModifier = modifiers.reduce((sum, m) => sum + m.delta, 0);
  const finalConfidence = candidate.baseConfidence + totalModifier;

  return {
    ...candidate,
    modifiers,
    finalConfidence,
  };
}

async function createCandidate(
  source: CandidateSource,
): Promise<RootCandidate> {
  const absolutePath = path.resolve(source.value!);
  const exists = await pathExists(absolutePath);
  const isDir = exists ? await isDirectory(absolutePath) : false;

  const candidate: RootCandidate = {
    path: absolutePath,
    source: source.source,
    baseConfidence: source.baseConfidence,
    modifiers: [],
    finalConfidence: source.baseConfidence,
    exists,
    isDirectory: isDir,
  };

  // Only apply modifiers if the path exists and is a directory
  if (exists && isDir) {
    return applyModifiers(candidate);
  }

  return candidate;
}

export async function resolveRootPath(
  options?: RootResolverOptions,
): Promise<RootCandidate> {
  const logger = options?.logger;
  const sources = collectCandidateSources(options);

  // Create candidates from all sources
  const candidates = await Promise.all(sources.map(createCandidate));

  logger?.debug(
    {
      candidates: candidates.map((c) => ({
        path: c.path,
        source: c.source,
        baseConfidence: c.baseConfidence,
        finalConfidence: c.finalConfidence,
        exists: c.exists,
        isDirectory: c.isDirectory,
        modifiers: c.modifiers,
      })),
    },
    'Collected root path candidates',
  );

  // Filter to valid directories only
  const validCandidates = candidates.filter((c) => c.exists && c.isDirectory);

  if (validCandidates.length === 0) {
    const attempted = candidates.map((c) => c.path).join(', ');
    throw new Error(
      `No valid root directory found. Attempted: ${attempted}. Ensure the directory exists and is accessible.`,
    );
  }

  // Define source priority for tie-breaking
  const sourcePriority: Record<RootCandidate['source'], number> = {
    'cli-arg': 1,
    'env-coderule-root': 2,
    'env-workspace': 3,
    'process-cwd': 4,
  };

  // Sort by confidence (highest first), then by source priority (lowest number wins)
  validCandidates.sort((a, b) => {
    const confidenceDiff = b.finalConfidence - a.finalConfidence;
    if (confidenceDiff !== 0) {
      return confidenceDiff;
    }
    // Tie-breaker: use source priority
    return sourcePriority[a.source] - sourcePriority[b.source];
  });

  const best = validCandidates[0]!;

  // Warn if confidence is low
  if (best.finalConfidence < 0.7) {
    logger?.warn(
      {
        path: best.path,
        source: best.source,
        confidence: best.finalConfidence,
        modifiers: best.modifiers,
      },
      'Selected root path has low confidence',
    );
  } else {
    logger?.info(
      {
        path: best.path,
        source: best.source,
        confidence: best.finalConfidence,
        modifiers: best.modifiers,
      },
      'Resolved root path',
    );
  }

  return best;
}
